import { useState, useEffect } from 'react'
import { Box, Card, CardContent, Typography, Grid, Chip, Button, Divider, Alert } from '@mui/material'
import { Warning, CheckCircle, Schedule, Gavel, ArrowForward, AutorenewOutlined } from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { usePlantContext } from '../hooks/usePlantContext'
import { useApi } from '../hooks/useApi'
import { getGreeting, formatDate } from '../utils/format'
import FreshnessIndicator from '../components/common/FreshnessIndicator'
import LoadingState from '../components/common/LoadingState'

function ExceptionCard({ title, count, icon: Icon, color, items }) {
  const hasIssues = count > 0
  return (
    <Card variant="outlined" sx={{ flex: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Icon sx={{ color: hasIssues ? `${color}.main` : 'success.main', fontSize: 24 }} />
          <Typography variant="h5">{title}</Typography>
        </Box>
        <Typography variant="h2" sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 700, mb: 1 }}>
          {count}
        </Typography>
        {hasIssues ? (
          <Typography variant="body2" color="text.secondary">{items}</Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2" color="success.main">All clear</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

// fallback demo data — used when API is unavailable or returns empty
const FALLBACK_EXCEPTIONS = [
  { title: 'Items Below DOS', count: 4, icon: Warning, color: 'error', items: 'FINEST CHILI BNS (49.9 DOS, target 60)' },
  { title: 'Capacity Alerts', count: 2, icon: Schedule, color: 'warning', items: 'Apr L1 (112%), Jun L2 (103%)' },
  { title: 'Pending Actions', count: 1, icon: Schedule, color: 'warning', items: '1 unapproved plan' },
]

const FALLBACK_FRESHNESS = [
  { name: 'Inventory', state: 'fresh', age: '2h' },
  { name: 'Forecast', state: 'aging', age: '26d' },
  { name: 'Master Data', state: 'aging', age: '3d' },
]

const FALLBACK_ACTIVITY = [
  { user: 'M. Simon', action: 'Edited HV CHILI BNS — Apr scheduled qty', time: '2h ago' },
  { user: 'M. Simon', action: 'Created scenario "GMI Soups — Full Volume"', time: '3h ago' },
  { user: 'H. Allmann', action: 'Approved Budget 2026 — Base Plan', time: '9d ago' },
]

const FALLBACK_ADVISORIES = [
  { count: '3', color: 'warning', text: 'Sequencing advisories — Line 1 April window has back-to-back changeovers exceeding 6 hrs' },
  { count: '1', color: 'error', text: 'Minimum quantity warning — FINEST CHILI BNS Apr run is below 200-case minimum' },
]

export default function Dashboard() {
  const { user } = useAuth()
  const { plant } = usePlantContext()
  const { loading, error, get } = useApi()
  const firstName = user?.displayName?.split(' ')[0] || 'Planner'

  const [exceptions, setExceptions] = useState(FALLBACK_EXCEPTIONS)
  const [freshnessSources, setFreshnessSources] = useState(FALLBACK_FRESHNESS)
  const [activity, setActivity] = useState(FALLBACK_ACTIVITY)
  const [advisories, setAdvisories] = useState(FALLBACK_ADVISORIES)

  useEffect(() => {
    let cancelled = false
    get(`/dashboard?plant=${plant.plantCode}`)
      .then((data) => {
        if (cancelled || !data) return

        if (data.exceptions) {
          const exc = data.exceptions
          setExceptions([
            { title: 'Items Below DOS', count: exc.itemsBelowDos?.count || 0, icon: Warning, color: 'error', items: exc.itemsBelowDos?.items || '' },
            { title: 'Capacity Alerts', count: exc.capacityAlerts?.count || 0, icon: Schedule, color: 'warning', items: exc.capacityAlerts?.items || '' },
            { title: 'Pending Actions', count: exc.pendingActions?.count || 0, icon: Schedule, color: 'warning', items: exc.pendingActions?.count ? `${exc.pendingActions.count} unapproved plan${exc.pendingActions.count > 1 ? 's' : ''}` : '' },
          ])
        }

        if (data.dataFreshness && typeof data.dataFreshness === 'object') {
          const df = data.dataFreshness
          const nameMap = { inventory: 'Inventory', forecast: 'Forecast', masterdata: 'Master Data' }
          const sources = Object.entries(df).map(([key, val]) => ({
            name: nameMap[key] || key,
            state: val.state || 'aging',
            age: val.age || '—',
          }))
          if (sources.length > 0) setFreshnessSources(sources)
        }

        if (Array.isArray(data.recentActivity) && data.recentActivity.length > 0) {
          setActivity(data.recentActivity.map(a => ({
            user: a.userName || a.user || '—',
            action: a.action || '',
            time: a.timestamp || a.time || '',
          })))
        }

        if (data.ruleAdvisories && data.ruleAdvisories.totalFired > 0) {
          const byType = data.ruleAdvisories.byType || {}
          setAdvisories(Object.entries(byType).map(([type, count]) => ({
            count: String(count),
            color: type.includes('warning') ? 'error' : 'warning',
            text: `${type.replace(/_/g, ' ')} (${count} active)`,
          })))
        }
      })
      .catch(() => {
        // API unavailable — keep fallback demo data
      })
    return () => { cancelled = true }
  }, [plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingState message="Loading dashboard..." />

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        {getGreeting()}, {firstName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {formatDate(new Date().toISOString())} — {plant.name}
      </Typography>

      {error && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          Using demo data — API unavailable ({error})
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {exceptions.map((exc, i) => (
          <Grid key={i} size={{ xs: 12, md: 4 }}>
            <ExceptionCard
              title={exc.title}
              count={exc.count}
              icon={exc.icon}
              color={exc.color}
              items={exc.items}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2 }}>Data Freshness</Typography>
              <FreshnessIndicator sources={freshnessSources} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2 }}>Recent Activity</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {activity.map((entry, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={entry.user} size="small" variant="outlined" />
                    <Typography variant="body2" sx={{ flex: 1 }}>{entry.action}</Typography>
                    <Typography variant="caption" color="text.secondary">{entry.time}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Gavel sx={{ color: 'warning.main', fontSize: 22 }} />
            <Typography variant="h5">Rule Advisories</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {advisories.map((adv, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={adv.count} size="small" color={adv.color} />
                <Typography variant="body2">{adv.text}</Typography>
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" endIcon={<ArrowForward />}>View Details</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
        <AutorenewOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          Dashboard auto-refreshes every 5 minutes
        </Typography>
      </Box>
    </Box>
  )
}
